import { z } from 'zod';
import * as dt from '../data.types.js';
import {
  BaseResponseSchema,
  dateSchema,
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  phoneNumberSchema,
  uuidSchema,
} from './primitives.js';

const providerUserRoleSchema = z.enum([
  dt.ALL_USER_ROLES.provider_admin,
  dt.ALL_USER_ROLES.provider_engineer,
  dt.ALL_USER_ROLES.provider_support,
  dt.ALL_USER_ROLES.provider_deployment_engineer,
]);

export const CreateProviderUserRequestSchema = z.object({
  email: emailSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  phone: phoneNumberSchema.optional(),
  role: providerUserRoleSchema,
  sendInvitation: z.boolean().optional().default(true),
});

export const UpdateProviderUserRequestSchema = z.object({
  firstName: firstNameSchema.optional(),
  lastName: lastNameSchema.optional(),
  phone: phoneNumberSchema.optional(),
  role: providerUserRoleSchema.optional(),
});

export const GetProviderUsersRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  role: providerUserRoleSchema.optional(),
  isActive: z.coerce.boolean().optional(),
  sortBy: z.enum(['email', 'firstName', 'lastName', 'createdAt']).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional(),
});

export const ChangeProviderUserRoleRequestSchema = z.object({
  role: providerUserRoleSchema,
});

export const ChangeProviderUserStatusRequestSchema = z.object({
  isActive: z.boolean(),
});

export const ProviderUserSchema = z.object({
  id: z.number().int(),
  /** Cognito `sub` — required for site access grants and JWT identity. */
  cognitoSub: uuidSchema,
  email: emailSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  phone: phoneNumberSchema.nullable().optional(),
  role: providerUserRoleSchema,
  isActive: z.boolean(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  lastLoginAt: dateSchema.nullable().optional(),
});

export const ProviderUsersListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    users: z.array(ProviderUserSchema),
    pagination: z.object({
      total: z.number().int().min(0),
      page: z.number().int().min(1),
      limit: z.number().int().min(1),
      totalPages: z.number().int().min(0),
    }),
  }),
});

export const ProviderUserResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    user: ProviderUserSchema,
  }),
});

export const CreateProviderUserResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    user: ProviderUserSchema,
    temporaryPassword: z.string(),
  }),
});

export const ResendProviderUserInvitationResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    email: emailSchema,
  }),
});
// Inferred types
export type ChangeProviderUserRoleRequest = z.infer<typeof ChangeProviderUserRoleRequestSchema>;
export type ChangeProviderUserStatusRequest = z.infer<typeof ChangeProviderUserStatusRequestSchema>;
export type CreateProviderUserRequest = z.infer<typeof CreateProviderUserRequestSchema>;
export type CreateProviderUserResponse = z.infer<typeof CreateProviderUserResponseSchema>;
export type GetProviderUsersRequest = z.infer<typeof GetProviderUsersRequestSchema>;
export type ProviderUser = z.infer<typeof ProviderUserSchema>;
export type ProviderUserResponse = z.infer<typeof ProviderUserResponseSchema>;
export type ProviderUsersListResponse = z.infer<typeof ProviderUsersListResponseSchema>;
export type ResendProviderUserInvitationResponse = z.infer<typeof ResendProviderUserInvitationResponseSchema>;
export type UpdateProviderUserRequest = z.infer<typeof UpdateProviderUserRequestSchema>;
